import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { Button } from 'ui-components/components/button/';
import { Checkbox } from 'ui-components/components/checkbox/';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';

import { useEnableAnalyticsRoleSelfService } from '../../../feature-flags/hooks/use-enable-analytics-role-self-service';
import { getLink } from '../../../services/link-service';
import { linkIDs } from '../../../services/link-service/links';
import { UserPermissionService as Roles } from '../../../services/user-permission-service';

import messages from './messages';
import styles from './styles.module.less';
import { AnalyticsLicenseCountDisplay } from './components/analytics-license-count-display';
import { UserCountDisplay } from './components/user-count-display';
import UserDateFormat from '../../../components/user-date-timezone-format';
import Global from '../../../store/global-selectors';
import { updateUsersList } from '../../../store/user/user.actions';
import { getSellerUsers } from '../../../store/user/user.api';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import Modal from '../components/user-management-modal/';
import { useUserLimitData } from '../payment-and-subscription/hooks/use-plan-details';

const modalStates = {
  closed: 'closed',
  openEdit: 'openEdit',
  openCreate: 'openCreate',
};

const locked = 'Locked';
const emailLock = 'EmailLock';
const active = 'Active';
const inactive = 'Inactive';

const statusToMessageDescriptor = {
  [locked]: messages.locked,
  [emailLock]: messages.emailLock,
  [active]: messages.active,
  [inactive]: messages.inactive,
};

const sortByUserNameAsc = (a, b) => {
  if (a.username < b.username) {
    return -1;
  }
  if (a.username > b.username) {
    return 1;
  }
  return 0;
};

export const UserManagement = () => {
  const [modalState, setModalState] = useState(modalStates.closed);
  const [focusedUserId, setFocusedUserId] = useState(undefined);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  const openModalEditMode = useCallback(
    (e) => {
      setFocusedUserId(e.target.id);
      setModalState(modalStates.openEdit);
    },
    [setModalState, setFocusedUserId],
  );
  const openModalCreateMode = useCallback(() => {
    setModalState(modalStates.openCreate);
  }, [setModalState]);
  const closeModal = useCallback(() => {
    setFocusedUserId(undefined);
    setModalState(modalStates.closed);
  }, [setModalState, setFocusedUserId]);
  const toggleInactiveUsers = useCallback(() => {
    setShowInactiveUsers((show) => !show);
  }, [setShowInactiveUsers]);

  const dispatch = useDispatch();
  const refreshUserGrid = useCallback(() => {
    getSellerUsers()
      .then((response) => {
        dispatch(updateUsersList(response.data));
      })
      .catch();
  }, [dispatch]);

  const isAnalyticsRoleSelfServiceEnabled = useEnableAnalyticsRoleSelfService();
  const intl = useIntl();
  const sessionUserId = useSelector(Global.selectUserCurrentUserId);
  const supportCountry = useSelector(Global.selectSellerSupportCountry);
  const userRolesHelpHref = getLink(linkIDs.UserRoles, supportCountry, intl.locale);
  const users = useSelector(Global.selectSellerUsers);
  const activeUserCount = users.filter((u) => u.isActive).length;
  const { totalUserCount, hasUnlimitedUsers } = useUserLimitData();
  const atUserLimit = !hasUnlimitedUsers && activeUserCount >= totalUserCount;

  return (
    <>
      {modalState !== modalStates.closed && (
        <Modal
          isEditMode={!!focusedUserId}
          onClose={closeModal}
          refreshUserGrid={refreshUserGrid}
          sessionUserId={sessionUserId}
          user={users.find((u) => u.userId === focusedUserId)}
        />
      )}
      <div className={styles.content}>
        <SettingsPageTitle value={messages.header} />
        <div className={styles.description}>
          <FormattedMessage
            {...messages.description}
            values={{
              link: (chunks) => (
                <a href={userRolesHelpHref} rel='noopener noreferrer' target='_blank'>
                  {chunks}
                </a>
              ),
            }}
          />
        </div>
        <FlexRow className={styles.displayCards} gap={Gap.Space16}>
          <UserCountDisplay />
          {isAnalyticsRoleSelfServiceEnabled && <AnalyticsLicenseCountDisplay />}
        </FlexRow>
        <div className={styles.gridHeader}>
          <Checkbox
            checked={showInactiveUsers}
            label={<FormattedMessage {...messages.showInactiveUsers} />}
            onChange={toggleInactiveUsers}
          />
          <Button disabled={atUserLimit} onClick={openModalCreateMode} theme='primary'>
            <FontAwesomeIcon className={styles.icon} icon={faPlus} />
            <FormattedMessage {...messages.addUser} />
          </Button>
        </div>
        <div className={classnames(styles.grid, { [styles['with-analytics-column']]: isAnalyticsRoleSelfServiceEnabled })}>
          <div className={styles['header-cell']}>
            <FormattedMessage {...messages.username} />
          </div>
          <div className={styles['header-cell']}>
            <FormattedMessage {...messages.name} />
          </div>
          <div className={styles['header-cell']}>
            <FormattedMessage {...messages.email} />
          </div>
          <div className={styles['header-cell']}>
            <FormattedMessage {...messages.lastLogin} />
          </div>
          {isAnalyticsRoleSelfServiceEnabled && (
            <div className={styles['header-cell']}>
              <FormattedMessage {...messages.analyticsAccess} />
            </div>
          )}
          <div className={styles['header-cell']}>
            <FormattedMessage {...messages.status} />
          </div>
          <div className={styles['header-cell']} />
          {users
            .filter((u) => (showInactiveUsers && !u.isActive) || u.isActive)
            .sort(sortByUserNameAsc)
            .map(({ userId, username, name, email, lastLoginDate, status, roles }) => {
              const statusMessageDescriptor = statusToMessageDescriptor[status] || statusToMessageDescriptor[active];
              return (
                <React.Fragment key={userId}>
                  <div className={styles.cell}>{username}</div>
                  <div className={styles.cell}>{name}</div>
                  <div className={styles.cell}>{email}</div>
                  <div className={styles.cell}>
                    <UserDateFormat dateTime={lastLoginDate} />
                  </div>
                  {isAnalyticsRoleSelfServiceEnabled && (
                    <div className={styles.cell}>
                      {Roles.userHasAnyAnalyticsRole(roles ?? []) ? (
                        <FontAwesomeIcon className={styles['analytics-check']} icon={faCheck} />
                      ) : (
                        '--'
                      )}
                    </div>
                  )}
                  <div className={styles.cell}>
                    <FormattedMessage {...statusMessageDescriptor} />
                  </div>
                  <div className={styles.cell}>
                    <Button id={userId} onClick={openModalEditMode} theme='link'>
                      <FormattedMessage {...messages.edit} />
                    </Button>
                  </div>
                </React.Fragment>
              );
            })}
        </div>
      </div>
    </>
  );
};
